import { useLayoutEffect, useRef } from "react";

const AT_BOTTOM_SLOP_PX = 2;

/**
 * Keeps a newly selected chip in view inside the capped chips container.
 *
 * The container has a fixed max height, and base-ui never scrolls a chip into
 * view — appending one just extends the scroll height, so the newest chip lands
 * below the fold. Following the bottom only while the reader is already there
 * surfaces the new chip without yanking anyone who scrolled up to remove an
 * earlier one.
 *
 * @param {number} count Number of selected values; growth is what triggers a scroll.
 */
export function useStickyChipScroll(count) {
  const ref = useRef(null);
  const atBottom = useRef(true);
  const previousCount = useRef(count);

  useLayoutEffect(() => {
    const container = ref.current;
    const grew = count > previousCount.current;
    previousCount.current = count;

    // The container unmounts with the popup, so there's nothing to scroll while
    // the filter is closed.
    if (!container || !grew || !atBottom.current) return;

    container.scrollTop = container.scrollHeight;
  }, [count]);

  const onScroll = event => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    atBottom.current =
      scrollHeight - scrollTop - clientHeight < AT_BOTTOM_SLOP_PX;
  };

  return { ref, onScroll };
}
